import RequestRefundSection from '@/components/sideNavigation/Cs/chatMessage/RequestRefundSection';
import { getChatType } from '@/constants/cs';
import {
  ChatMessage,
  PackageAccept,
  RefundCreateAccept,
  RefundCreateConfirmed,
  RefundCreateRejected,
  TextRequest,
  TextResponse,
} from '@/types/cs.type';
import { format, subDays } from 'date-fns';
import styled, { css } from 'styled-components';
import ICON_KTP_USER from '@/assets/cs/icon_ktp_user.png';
import ICON_KTP_ADMIN from '@/assets/cs/Icon_ktp_admin.png';
import RequestPackageSection from '@/components/sideNavigation/Cs/chatMessage/RequestPackageSection';
import ICON_REFUND from '@/assets/cs/icon_refund.png';
import ICON_PACKAGE from '@/assets/cs/icon_package.png';
import ResultRefundSection from '@/components/sideNavigation/Cs/chatMessage/ResultRefundSection';

type Props = {
  chat?: ChatMessage;
  chatMessageType?: 'RECEIVE' | 'SEND';
  requestType?: 'REFUND' | 'PACKAGE';
  refundInfo?: RefundCreateAccept;
  isHiddenTime?: boolean;
};

function CsChatMessageItem({
  chat,
  chatMessageType = 'SEND',
  requestType,
  refundInfo,
  isHiddenTime = false,
}: Props) {
  if (requestType) {
    return (
      <>
        <ChatItem type='SEND'>
          <ChatTime>{format(new Date(), 'HH:mm')}</ChatTime>
          <ChatContent type='SEND'>
            <RequestChatContent>
              <img
                src={requestType === 'PACKAGE' ? ICON_PACKAGE : ICON_REFUND}
                alt=''
                width={24}
              />
              {requestType === 'PACKAGE' ? '소모품 신청' : '환급건 등록'}
            </RequestChatContent>
          </ChatContent>
        </ChatItem>
        <ChatItem type='RECEIVE'>
          <img src={ICON_KTP_USER} alt='' width={28} />
          <ChatContent type='RECEIVE'>
            {requestType === 'PACKAGE' ? (
              <RequestPackageSection />
            ) : (
              <RequestRefundSection />
            )}
          </ChatContent>
        </ChatItem>
      </>
    );
  }

  if (
    chat?.type.includes('TEXT') &&
    !(chat as TextRequest | TextResponse).content
  ) {
    return null;
  }

  const chatContentRenderMap: Record<string, any> = {
    TEXT_REQUEST: (chat as TextRequest).content,
    ADMIN_TEXT_REQUEST: (chat as TextRequest).content,
    TEXT_RESPONSE: (chat as TextResponse).content,
    APPEND_TEXT_REQUEST: (chat as TextRequest).content,
    REFUND_CREATE_REQUESTED: (
      <RequestChatContent>
        <img src={ICON_REFUND} alt='' width={24} />
        환급건 등록
      </RequestChatContent>
    ),
    REFUND_CREATE_ACCEPTED: (
      <ResultRefundSection
        type={(chat as RefundCreateAccept).type}
        refundInfo={refundInfo}
      />
    ),
    REFUND_CREATE_CONFIRMED: (
      <ResultRefundSection
        type={(chat as RefundCreateConfirmed).type}
        refundInfo={refundInfo}
      />
    ),
    REFUND_CREATE_REJECTED: (
      <ResultRefundSection
        type={(chat as RefundCreateRejected).type}
        refundInfo={refundInfo}
        reasonResult={(chat as RefundCreateRejected).reason || ''}
      />
    ),
    PACKAGE_REQUESTED: (
      <RequestChatContent>
        <img src={ICON_PACKAGE} alt='' width={24} />
        소모품 신청
      </RequestChatContent>
    ),
    PACKAGE_ACCEPTED: <RequestPackageSection data={chat as PackageAccept} />,
  };

  const getTime = (dateTime: string) => {
    if (
      format(new Date(dateTime), 'yyyy-MM-dd') ===
      format(new Date(), 'yyyy-MM-dd')
    ) {
      return format(new Date(dateTime), 'HH:mm');
    }

    if (
      format(new Date(dateTime), 'yyyy-MM-dd') ===
      format(subDays(new Date(), 1), 'yyyy-MM-dd')
    ) {
      return `어제 ${format(new Date(dateTime), 'HH:mm')}`;
    }

    return format(new Date(dateTime), 'M월 d일 HH:mm');
  };

  if (chat) {
    return (
      <ChatItem type={chatMessageType}>
        {!isHiddenTime && (
          <ChatInfo>
            {(chat.type ? getChatType(chat.type) : 'SEND') === 'RECEIVE' && (
              <img
                src={
                  chat.type === 'ADMIN_TEXT_REQUEST'
                    ? ICON_KTP_ADMIN
                    : ICON_KTP_USER
                }
                alt=''
                width={28}
              />
            )}
            <ChatTime>{getTime(chat.createdAt)}</ChatTime>
          </ChatInfo>
        )}
        <ChatContent type={chatMessageType}>
          {chatContentRenderMap[chat.type]}
        </ChatContent>
      </ChatItem>
    );
  }

  return null;
}

const ChatItem = styled.li<{ type: string }>`
  display: flex;
  flex-direction: column;
  animation: slide-up 300ms ease-in-out forwards;
  &:not(:first-child) {
    margin-top: 15px;
  }

  ${({ type }) =>
    type === 'SEND'
      ? css`
          align-items: flex-end;
        `
      : css`
          align-items: flex-start;
        `}

  @keyframes slide-up {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
`;

const ChatInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ChatTime = styled.span`
  color: #80848a;
  font-size: 12px;
  font-weight: 500;
  line-height: 150%;
`;

const ChatContent = styled.p<{ type: string }>`
  padding: 10px 12px;
  font-size: 16px;
  font-weight: 400;
  line-height: 150%;

  ${({ type }) =>
    type === 'SEND'
      ? css`
          margin-top: 2px;
          border-radius: 10px 10px 0px 10px;
          color: #fff;
          background: linear-gradient(99deg, #206cff -9.06%, #6c9dfe 117.81%);
        `
      : css`
          margin-top: 6px;
          border-radius: 0px 10px 10px 10px;
          color: #3a3b3e;
          background: #f5f6f7;
        `}
`;

const RequestChatContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 2px;
`;

export default CsChatMessageItem;
